import {Group, NumberInput, SegmentedControl, Stack, Text} from '@mantine/core'
import {DateInput} from '@mantine/dates'
import {addDays} from 'date-fns'

import {businessCalendarDate} from 'src/api/business-time'
import {parseDateInputValue} from 'src/jobs/job-schedule-format'

import {
    DAYS_PER_MONTH_WITHOUT_START,
    isValidRentalPeriod,
    type QuoteRentalPeriod,
    rentalPeriodDays,
} from './quote-builder-logic'

type PeriodMode = 'none' | 'months' | 'dates'

type QuoteRentalPeriodFieldProps = {
    value: QuoteRentalPeriod | null | undefined
    onChange: (value: QuoteRentalPeriod | null) => void
    readOnly?: boolean
}

const MONTHS_PERIOD_NOTE =
    `Each month counts as ${DAYS_PER_MONTH_WITHOUT_START} days. Monthly items are charged per ` +
    '28-day cycle, rounded up.'

function rentalPeriodSummary(period: QuoteRentalPeriod | null | undefined): string | null {
    if (period == null || !isValidRentalPeriod(period)) {
        return null
    }

    const days = rentalPeriodDays(period)
    const length =
        period.kind === 'months'
            ? `${days} days (${DAYS_PER_MONTH_WITHOUT_START} a month).`
            : `${days} ${days === 1 ? 'day' : 'days'}.`
    return `${length} Period lines are charged per day, 7-day or 28-day cycle, rounded up.`
}

export function QuoteRentalPeriodField({value, onChange, readOnly}: QuoteRentalPeriodFieldProps) {
    const mode: PeriodMode = value == null ? 'none' : value.kind

    function changeMode(next: string) {
        if (next === 'months') {
            onChange({kind: 'months', months: 1})
        } else if (next === 'dates') {
            const start = businessCalendarDate()
            onChange({kind: 'dates', startDate: start, endDate: addDays(start, 27)})
        } else {
            onChange(null)
        }
    }

    const summary = rentalPeriodSummary(value)
    const datesBackwards = value?.kind === 'dates' && !isValidRentalPeriod(value)

    return (
        <Stack gap={6}>
            <Text size="sm" fw={500}>
                Rental period
            </Text>
            <SegmentedControl
                size="xs"
                value={mode}
                onChange={changeMode}
                disabled={readOnly}
                data={[
                    {value: 'none', label: 'None'},
                    {value: 'months', label: 'Number of months'},
                    {value: 'dates', label: 'Start and end dates'},
                ]}
                style={{alignSelf: 'flex-start'}}
            />
            {value?.kind === 'months' && (
                <Group gap="sm" align="flex-end" wrap="nowrap">
                    <NumberInput
                        size="sm"
                        label="Months"
                        min={1}
                        allowDecimal={false}
                        value={value.months}
                        readOnly={readOnly}
                        onChange={(months) =>
                            onChange({
                                kind: 'months',
                                months: typeof months === 'number' ? months : 0,
                            })
                        }
                        w={160}
                        style={{flex: 'none'}}
                    />
                    <Text size="sm" fw={500} pb={6} data-testid="months-period-note">
                        {MONTHS_PERIOD_NOTE}
                    </Text>
                </Group>
            )}
            {value?.kind === 'dates' && (
                <Group gap="sm" align="flex-start">
                    <DateInput
                        size="sm"
                        label="Start date"
                        value={value.startDate}
                        readOnly={readOnly}
                        onChange={(startDate) => {
                            const parsed = parseDateInputValue(startDate)
                            if (parsed) {
                                onChange({...value, startDate: parsed})
                            }
                        }}
                    />
                    <DateInput
                        size="sm"
                        label="End date (last day)"
                        value={value.endDate}
                        readOnly={readOnly}
                        error={datesBackwards ? 'Ends before it starts' : undefined}
                        onChange={(endDate) => {
                            const parsed = parseDateInputValue(endDate)
                            if (parsed) {
                                onChange({...value, endDate: parsed})
                            }
                        }}
                    />
                </Group>
            )}
            {summary && (
                <Text size="xs" c="dimmed">
                    {summary}
                </Text>
            )}
        </Stack>
    )
}
